import { ReactNode, useState } from 'react';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { FormError } from '@components/form-error';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconAIMagic } from '@components/icons/ai-magic';
import { IconTriggerOff } from '@components/icons/trigger-off';
import { SizedRelativeSpinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { FieldMappingGroup } from '@components/text-editors/addons/field-mapping';
import { showError } from '@components/toaster';
import { Tooltip } from '@components/tooltip';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { fileNameFromPath } from '@utils/strings';
import { uuid } from '@utils/uuid';

import { useGenerateAIAgentOutputFromInstructions } from 'api/ai-agents/hooks';
import { useWebSocket } from 'api/hooks';
import { AIModel, VariableValue } from 'api/types';

import { Output, Property } from './output-list';

import { supportsFileSearch, supportsWebSearch } from '../ai-agent-config';

export type UploadedFile = {
  id?: string;
  name: string;
  url: string;
};

type FormLike = any;

export const fileUrlsToUploadedFiles = (files?: string[]): UploadedFile[] =>
  (files ?? []).map((file) => ({
    id: file,
    name: fileNameFromPath(file),
    url: file,
  }));

type AIAgentConfigurationFieldsProps = {
  Form: FormLike;
  defaultModel: string;
  resolvedModel: string;
  defaultSystemInstructions?: string;
  defaultReasoningEffort?: string;
  reasoningEffortConfig?: {
    default: string;
    options: Array<{ label: string; value: string }>;
  };
  onOpenModelSelector: () => void;
  renderModelSelector: (props: { model: string; onOpen: () => void }) => ReactNode;
  isTemplate?: boolean;
};

export const AIAgentConfigurationFields = ({
  Form,
  defaultModel,
  resolvedModel,
  defaultSystemInstructions,
  defaultReasoningEffort,
  reasoningEffortConfig,
  onOpenModelSelector,
  renderModelSelector,
  isTemplate = false,
}: AIAgentConfigurationFieldsProps) => {
  const { t } = useScopedTranslate(locale);

  return (
    <Stack space={4}>
      <Form.HiddenField id="model" defaultValue={defaultModel} validators={['required']} />
      {renderModelSelector({ model: resolvedModel, onOpen: onOpenModelSelector })}
      <Form.Textarea
        id="systemInstructions"
        placeholder={isTemplate ? t('systemInstructionsPlaceholder') : undefined}
        label={t('systemInstructions')}
        defaultValue={defaultSystemInstructions}
        validators={['required']}
        isExpandable
      />
      {reasoningEffortConfig && (
        <Form.Select
          id="reasoningEffort"
          label={t('reasoningEffort')}
          defaultValue={defaultReasoningEffort ?? reasoningEffortConfig.default}
          options={reasoningEffortConfig.options}
          validators={['required']}
          isClearable={false}
        />
      )}
    </Stack>
  );
};

type AIAgentKnowledgeFilesFieldProps = {
  Form: FormLike;
  isTemplate?: boolean;
  fieldId: string;
  defaultFiles?: UploadedFile[];
  onSelect: (file: File) => Promise<any>;
  helperText?: string;
  hideLabel?: boolean;
  additionalFilesConfig?: {
    fieldId: string;
    variableGroups: FieldMappingGroup[];
    defaultValue?: VariableValue[];
    fieldMappingId: string;
  };
};

export const AIAgentKnowledgeFilesField = ({
  Form,
  isTemplate = false,
  fieldId,
  defaultFiles,
  onSelect,
  helperText,
  hideLabel = false,
  additionalFilesConfig,
}: AIAgentKnowledgeFilesFieldProps) => {
  const { t } = useScopedTranslate(locale);

  return (
    <Stack space={4}>
      {helperText && <Text font="regular14" text={helperText} />}
      <Form.InstantUploadMultiFileField
        // @ts-ignore-next-line
        id={fieldId}
        label={isTemplate ? t('files') : t('knowledgeFiles')}
        onSelect={onSelect}
        defaultValue={defaultFiles}
        hideLabel={isTemplate || hideLabel}
      />
      {!isTemplate && additionalFilesConfig && (
        <Form.FieldMappingFileListField
          id={additionalFilesConfig.fieldId}
          label={t('additionalFiles')}
          variableGroups={additionalFilesConfig.variableGroups}
          defaultValue={additionalFilesConfig.defaultValue}
          onSelect={onSelect}
          fieldMappingId={additionalFilesConfig.fieldMappingId}
        />
      )}
    </Stack>
  );
};

type AIAgentToolFieldProps = {
  Form: FormLike;
  fieldId: string;
  isSupported: boolean;
  label: string;
  instructions: string;
  unavailableReason: string;
  dataTestid: string;
  defaultValue?: boolean;
};

const AIAgentToolField = ({
  Form,
  fieldId,
  isSupported,
  label,
  instructions,
  unavailableReason,
  dataTestid,
  defaultValue,
}: AIAgentToolFieldProps) => {
  return (
    <Tooltip content={isSupported ? undefined : unavailableReason}>
      <div data-testid={isSupported ? undefined : dataTestid}>
        <Card spaceX={3} spaceY={3}>
          <Stack space={2}>
            {isSupported ? (
              <Form.Switch id={fieldId} label={label} defaultValue={defaultValue ?? false} />
            ) : (
              <Switch label={label} value={false} isDisabled />
            )}
            <Text
              font="regular14"
              style={isSupported ? undefined : { opacity: 0.5 }}
              text={instructions}
            />
          </Stack>
        </Card>
      </div>
    </Tooltip>
  );
};

type AIAgentToolsFieldsProps = {
  Form: FormLike;
  model: AIModel;
  defaultUseWebSearch?: boolean;
  defaultUseFileSearch?: boolean;
};

export const AIAgentToolsFields = ({
  Form,
  model,
  defaultUseWebSearch,
  defaultUseFileSearch,
}: AIAgentToolsFieldsProps) => {
  const { t } = useScopedTranslate(locale);

  return (
    <Stack space={4}>
      <AIAgentToolField
        Form={Form}
        fieldId="useWebSearch"
        isSupported={supportsWebSearch(model)}
        label={t('useWebSearch')}
        instructions={t('useWebSearchInstructions')}
        unavailableReason={t('useWebSearchUnavailable')}
        dataTestid="use-web-search-unavailable"
        defaultValue={defaultUseWebSearch}
      />
      <AIAgentToolField
        Form={Form}
        fieldId="useFileSearch"
        isSupported={supportsFileSearch(model)}
        label={t('useFileSearch')}
        instructions={t('useFileSearchInstructions')}
        unavailableReason={t('useFileSearchUnavailable')}
        dataTestid="use-file-search-unavailable"
        defaultValue={defaultUseFileSearch}
      />
    </Stack>
  );
};

type AIAgentPromptFieldProps = {
  Form: FormLike;
  isTemplate?: boolean;
  variableGroups: FieldMappingGroup[];
  defaultValue?: VariableValue;
  fieldMappingId: string;
  aiOptions?: {
    generateChoices: Array<{ title: string; prompt: string }>;
    threadInstructions: string;
    useWebSocket: any;
  };
};

export const AIAgentPromptField = ({
  Form,
  isTemplate = false,
  variableGroups,
  defaultValue,
  fieldMappingId,
  aiOptions,
}: AIAgentPromptFieldProps) => {
  const { t } = useScopedTranslate(locale);

  if (isTemplate) {
    return null;
  }

  return (
    <Form.FieldMappingTextEditor
      id="prompt"
      label={t('prompt')}
      placeholder={t('promptPlaceholder')}
      variableGroups={variableGroups}
      defaultValue={defaultValue}
      plaintext
      validators={['required']}
      fieldMappingId={fieldMappingId}
      aiOptions={aiOptions}
    />
  );
};

type AIAgentOutputFieldsProps = {
  Form: FormLike;
  output: Property[];
  defaultOutput: Property[];
  systemInstructions: string;
  setOutput: (value: Property[]) => void;
  handleOutputModal: (index?: number) => void;
  handleDeleteOutput: (index: number) => void;
  hasOutputError?: unknown;
  isGenerateOutputDisabled: boolean;
};

export const AIAgentOutputFields = ({
  Form,
  output,
  defaultOutput,
  systemInstructions,
  setOutput,
  handleOutputModal,
  handleDeleteOutput,
  hasOutputError,
  isGenerateOutputDisabled,
}: AIAgentOutputFieldsProps) => {
  const { t } = useScopedTranslate(locale);
  const [websocketId, setWebsocketId] = useState('');
  const [isGeneratingOutput, setIsGeneratingOutput] = useState(false);
  const generateOutputMutation = useGenerateAIAgentOutputFromInstructions();

  useWebSocket(
    `ai-agents/generate-output/${websocketId}/`,
    (data: MessageEvent<string>) => {
      let payload: {
        content?: {
          status?: 'success' | 'error';
          hasError?: boolean;
          message?: string;
          output?: Property[];
        };
      };
      try {
        payload = JSON.parse(data.data);
      } catch (_error) {
        showError(t('generateOutputFromInstructionsError'));
        setIsGeneratingOutput(false);
        setWebsocketId('');
        return;
      }
      const content = payload.content;
      if (!content) {
        setIsGeneratingOutput(false);
        setWebsocketId('');
        return;
      }

      if (content.status === 'error' || content.hasError) {
        showError(content.message || t('generateOutputFromInstructionsError'));
        setIsGeneratingOutput(false);
        setWebsocketId('');
        return;
      }

      if (content.status === 'success' && Array.isArray(content.output)) {
        setOutput(content.output);
        setIsGeneratingOutput(false);
        setWebsocketId('');
      }
    },
    { enabled: Boolean(websocketId) },
  );

  const handleGenerateOutputFromInstructions = async () => {
    if (isGeneratingOutput || isGenerateOutputDisabled) {
      return;
    }
    const nextWebsocketId = uuid();
    setWebsocketId(nextWebsocketId);
    setIsGeneratingOutput(true);

    try {
      await generateOutputMutation.mutateAsync({
        systemInstructions,
        websocketId: nextWebsocketId,
      });
    } catch (_error) {
      showError(t('generateOutputFromInstructionsError'));
      setIsGeneratingOutput(false);
      setWebsocketId('');
    }
  };

  return (
    <Stack space={4}>
      <Text font="regular14" text={t('outputInstructions')} />
      {hasOutputError && <FormError text={t('outputRequired')} />}
      <Stack space={2}>
        {output.length === 0 && (
          <Stack
            orientation="horizontal"
            align="center"
            space={2}
            style={{
              backgroundColor: theme.color.uiTrans5,
              borderRadius: pxToRem(8),
              border: `${pxToRem(1)} solid ${theme.color.border10}`,
              padding: `${pxToRem(16)} ${pxToRem(20)}`,
            }}
          >
            <IconTriggerOff color="textSecondary" />
            <Text font="regular14" text={t('noOutputConfigured')} color="textSecondary" />
          </Stack>
        )}
        <Output
          items={output}
          handleOutputModal={handleOutputModal}
          handleDeleteOutput={handleDeleteOutput}
          setValue={setOutput}
        />
        <Stack orientation="horizontal" space={2}>
          <Form.HiddenField id="output" validators={['required']} defaultValue={defaultOutput} />
          <Button
            variant="ai-gradient"
            text={
              isGeneratingOutput
                ? t('generateOutputFromInstructionsLoading')
                : t('generateOutputFromInstructions')
            }
            iconLeft={
              isGeneratingOutput ? (
                <SizedRelativeSpinner size={16} $skipMargin $borderWidth={1.5} />
              ) : (
                <IconAIMagic color="aiGradientTeal100" />
              )
            }
            isDisabled={isGenerateOutputDisabled || isGeneratingOutput}
            onClick={handleGenerateOutputFromInstructions}
            tooltip={
              isGenerateOutputDisabled ? t('generateOutputFromInstructionsDisabled') : undefined
            }
            textStyle={{
              background: 'linear-gradient(120deg, #005FFF 0%, #E039CE 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          />
          <Button
            variant="secondary"
            text={t('addOutput')}
            onClick={() => handleOutputModal()}
            iconLeft={<IconAddPlus />}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

const locale = {
  en: {
    model: 'Model',
    systemInstructions: 'System instructions',
    systemInstructionsPlaceholder: 'You are a helpful assistant...',
    reasoningEffort: 'Reasoning effort',
    files: 'Files',
    knowledgeFiles: 'Knowledge files',
    additionalFiles: 'Additional files',
    useWebSearch: 'Use web search',
    useWebSearchInstructions:
      'Enable this to allow your agent to use web search to find the most up-to-date information',
    useWebSearchUnavailable: 'Web search is only available on OpenAI models',
    useFileSearchUnavailable: 'File search is only available on OpenAI models',
    useFileSearch: 'Use file search for additional files',
    useFileSearchInstructions:
      'Enable for large documents or when searching for specific information. Disable for small files where the AI should read the complete content.',
    prompt: 'Prompt',
    promptPlaceholder: 'Write your prompt...',
    outputRequired: 'You need to add at least one output',
    noOutputConfigured: 'No output configured',
    outputInstructions: 'Define the data the model should generate',
    addOutput: 'Add manually',
    generateOutputFromInstructions: 'Generate from instructions',
    generateOutputFromInstructionsLoading: 'Generating...',
    generateOutputFromInstructionsDisabled:
      'Add system instructions to enable generating structured output',
    generateOutputFromInstructionsError:
      'Could not generate output from instructions. Please try again.',
  },
};
